import { useState, useEffect, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { updatePassword, getSession } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'
import { Logo } from '@/components/Logo'

export function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [validSession, setValidSession] = useState<boolean | null>(null)
  const navigate = useNavigate()
  const { showToast } = useToast()

  useEffect(() => {
    // Check if we have a valid session (from the email link)
    const checkSession = async () => {
      const session = await getSession()
      setValidSession(!!session || !!window.location.hash)
    }
    checkSession()
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (password.length < 8) {
      showToast('Password must be at least 8 characters', 'error')
      return
    }

    setLoading(true)

    try {
      await updatePassword(password)
      showToast('Password updated successfully!')
      setTimeout(() => {
        navigate('/dashboard')
      }, 1500)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to update password', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f6f6f8] dark:bg-[#111621] flex flex-col">
      {/* Floating Navbar */}
      <div className="fixed top-6 left-0 right-0 z-50 flex justify-center px-4">
        <header className="flex items-center justify-between w-full max-w-[480px] bg-white/80 dark:bg-[#1c2230]/80 backdrop-blur-md border border-gray-200 dark:border-gray-800 rounded-full px-6 py-2.5 shadow-sm">
          <Link to="/">
            <Logo size="md" variant="auto" />
          </Link>
          <Link
            to="/login"
            className="bg-[#1754cf] text-white text-sm font-bold px-5 py-2 rounded-full hover:opacity-90 transition-opacity"
          >
            Sign In
          </Link>
        </header>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12 pt-28">
        <div className="w-full max-w-[440px]">
          {/* Auth Card */}
          <div className="bg-white dark:bg-slate-900 shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-200 dark:border-slate-800 rounded-xl p-8">
            {/* Headline */}
            <div className="text-center mb-8">
              <h1 className="text-slate-900 dark:text-white text-3xl font-bold tracking-tight mb-2">
                Reset Password
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                Enter your new password below.
              </p>
            </div>

            {validSession === false ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-symbols-outlined text-red-600 dark:text-red-400 text-3xl">
                    error
                  </span>
                </div>
                <p className="text-red-500 mb-4">Invalid or expired link. Please request a new one.</p>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center gap-2 text-[#1754cf] text-sm font-semibold hover:underline"
                >
                  Request new reset link
                  <span className="material-symbols-outlined text-base">arrow_forward</span>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="flex flex-col gap-2">
                  <label className="text-slate-700 dark:text-slate-300 text-sm font-medium">
                    New Password
                  </label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">
                      lock
                    </span>
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full rounded-lg text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1754cf]/20 focus:border-[#1754cf] h-12 pl-10 pr-4 placeholder:text-slate-400 text-sm transition-all"
                      placeholder="Min. 8 characters"
                      minLength={8}
                      required
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1754cf] hover:bg-[#1754cf]/90 text-white font-semibold h-12 rounded-lg transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <span>Update Password</span>
                        <span className="material-symbols-outlined text-lg">check_circle</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Footer Link */}
          <div className="mt-8 text-center">
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Remember your password?{' '}
              <Link to="/login" className="text-slate-900 dark:text-white font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-slate-200 dark:border-slate-800 text-center">
        <p className="text-slate-400 text-xs">© {new Date().getFullYear()} URLzippy Inc. All rights reserved.</p>
      </footer>
    </div>
  )
}
